// PropertyDetailsPage.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MyContext } from '../../../../../Context/MyContext';
import PropertyDetails from './PropertyDetails';
import PropertyDetailsSkeleton from './PropertyDetailsSkeleton';
import API from '../../../../../Api/api';

const PropertyDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { properties, user, setUser, fetchPropertyById } = useContext(MyContext);
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  const cached = properties.find((p) => String(p.id) === String(id)) || null;
  const [property, setProperty] = useState(cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(false);

  // Cache-first: paint immediately from context when available, then refresh.
  // Do NOT re-enter loading when `properties` updates — that caused blank flashes.
  useEffect(() => {
    let cancelled = false;
    const propertyId = id;

    const loadProperty = async () => {
      const fromCache =
        propertiesRef.current.find((p) => String(p.id) === String(propertyId)) || null;

      if (fromCache) {
        setProperty(fromCache);
        setLoading(false);
        setError(false);
      } else {
        setLoading(true);
        setError(false);
      }

      const data = await fetchPropertyById(propertyId);
      if (cancelled) return;

      if (data) {
        setProperty(data);
        setError(false);
      } else if (!fromCache) {
        setProperty(null);
        setError(true);
      }
      setLoading(false);
    };

    loadProperty();
    return () => {
      cancelled = true;
    };
  }, [id, fetchPropertyById]);

  // 2. Track Recent Properties — backend for auth users, localStorage for guests
  useEffect(() => {
    if (!property?.id) return;

    const timer = setTimeout(async () => {
      if (user) {
        try {
          const token = localStorage.getItem('access_token');
          const res = await API.post(
            '/auth/track-recent',
            { property_id: property.id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data?.recent_property_id) {
            const updatedUser = { ...user, recent_property_id: res.data.recent_property_id };
            setUser(updatedUser);
            localStorage.setItem('loggedUser', JSON.stringify(updatedUser));
          }
        } catch (_) {
          const updatedRecent = [
            property.id,
            ...(user.recent_property_id || []).filter((rid) => rid !== property.id),
          ].slice(0, 20);
          const updatedUser = { ...user, recent_property_id: updatedRecent };
          setUser(updatedUser);
          localStorage.setItem('loggedUser', JSON.stringify(updatedUser));
        }
      } else {
        let guestRecent = [];
        try {
          guestRecent = JSON.parse(localStorage.getItem('guest_recent')) || [];
        } catch {
          guestRecent = [];
        }
        guestRecent = [property.id, ...guestRecent.filter((rid) => rid !== property.id)].slice(0, 20);
        localStorage.setItem('guest_recent', JSON.stringify(guestRecent));
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [property?.id, user?.id]); // eslint-disable-line

  if (loading) {
    return <PropertyDetailsSkeleton />;
  }

  if (error || !property) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center', color: '#888' }}>
        <p style={{ fontSize: '16px', marginBottom: '16px' }}>Property not found.</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: '1px solid #ddd',
            padding: '7px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '13px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          ← Back
        </button>
      </div>
    );
  }

  return <PropertyDetails property={property} />;
};

export default PropertyDetailsPage;
